/**
 * WhatsApp, which has two traps in it.
 *
 * First: a message encoded with %0A alone loses its line breaks on iOS. The
 * whole message arrives as one paragraph. Encoding CRLF survives everywhere.
 *
 * Second: window.open is honoured only from a direct click. Opening the chat
 * after an await means the browser has already decided the gesture is over and
 * blocks it. The fix is to open the window synchronously inside the handler
 * and set its location when the call comes back, which is what
 * openWhatsAppAfter does.
 */

/** Digits only, with the Israeli leading zero replaced by the country code. */
export function toWhatsAppNumber(phone: string | null | undefined, countryCode = '972'): string | null {
  if (!phone) return null;
  const digits = phone.replace(/\D/g, '');
  if (!digits) return null;
  if (digits.startsWith('00')) return digits.slice(2);
  if (digits.startsWith(countryCode)) return digits;
  if (digits.startsWith('0')) return `${countryCode}${digits.slice(1)}`;
  return digits;
}

/** Normalises line breaks to CRLF, then percent-encodes the whole message. */
export function encodeMessage(message: string): string {
  return encodeURIComponent(message.replace(/\r\n|\r|\n/g, '\r\n'));
}

export function whatsAppUrl(phone: string | null | undefined, message: string): string | null {
  const number = toWhatsAppNumber(phone);
  if (!number) return null;
  return `https://wa.me/${number}?text=${encodeMessage(message)}`;
}

/**
 * A share with no recipient: WhatsApp asks who to send it to.
 *
 * Used for a link the tutor hands out rather than sends to one person, where
 * there is no phone number to address it to and asking for one would be
 * inventing a step.
 */
export function whatsAppShareUrl(message: string): string {
  return `https://wa.me/?text=${encodeMessage(message)}`;
}

/**
 * Opens a chat whose contents are not known yet.
 *
 * Call this synchronously from the click handler. It opens a blank window
 * straight away, which the browser allows because the gesture is still live,
 * and points it at the chat once `build` resolves. If the work fails the
 * window is closed again rather than left blank.
 */
export async function openWhatsAppAfter(build: () => Promise<string | null>): Promise<void> {
  const chat = window.open('', '_blank');
  try {
    const url = await build();
    if (!url) {
      chat?.close();
      return;
    }
    if (chat) chat.location.href = url;
    else window.location.href = url;
  } catch (error) {
    chat?.close();
    throw error;
  }
}
